import _ from 'lodash'
import moment from 'moment'
import React, { useEffect } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { compose } from 'redux'
import { v4 } from 'uuid'

import { NEUTRAL_COLOURS } from 'constants/colors'
import { DAYS_OF_WEEK_LIST, DISPLAY_DATE_SHORT_MONTH_NAME_FORMAT, DISPLAY_SHORT_DATE_FORMAT } from 'constants/date'
import {
  ChildBooking,
  ChildProduct,
  ChildProductRegularAmountDiscount,
  ChildProductRegularLinkDiscount,
  ChildProductRegularPercentageDiscount,
  ChildProductRegularSession,
} from 'services/booking/childBooking/constants'
import { ChildProductTypes } from 'services/booking/childProducts/constants'
import { Child } from 'services/child/constants'
import { NurserySettings } from 'services/nurseries/models'

import appSelectors from 'services/app/selectors'
import { withChildBookingService, withChildBookingServiceProps } from 'services/booking/childBooking'
import childBookingSelectors from 'services/booking/childBooking/selectors'
import { withChildSessionsService, withChildSessionsServiceProps } from 'services/legacy/childSessions'
import * as childSessionsSelectors from 'services/legacy/childSessions/selectors'
import { getFormattedOpeningDays } from 'services/nurseries/single/selectors/single'
import { withRouter, withRouterProps } from 'services/router'

import { Banner, DefinitionList, Flex, Space, Spinner, Typography } from 'components'
import ChildSessionsList from 'module/Children/Child/ChildBookingPattern/ChildSessions/components/ChildSessionsList'

import { RootState } from 'core/reducers'
import SubdomainCurrencyProvider from 'providers/SubdomainCurrencyProvider'
import i18n from 'translations'

import { StyledFooter, StyledHeader, StyledWeekContainer, StyledWeekDay, StyledWrapper } from './BookingPatternStyled'

const CHILD_BOOKING_GROUPS = {
  read: [
    'child',
    'period',
    'childBooking',
    'childBooking.attendancePeriod',
    'childBooking.child',
    'childBooking.childProducts',
    'childBookingPreview.product',
    'regularChildProduct',
    'regularChildProduct.product',
    'nurserySessionProduct',
    'nurserySessionProduct.items',
    'nurseryDiscountProduct',
    'nurseryItemProduct',
    'nurseryFundingProduct',
    'oneOffChildProduct.product',
    'regularChildProduct.price',
    'regularChildProduct.settings',
    'regularChildProductSettings',
  ],
}

const mapState = (state: RootState, { childSessionsListState }) => ({
  childBookings: childBookingSelectors.getChildBookingListDataState(state),
  childSessions: childSessionsSelectors.getChildSessionsList(state),
  isFetchingSessions: appSelectors.getIsFetching(childSessionsListState),
})

const ALTERNATES = 2

const renderDummyChildBookingsV3 = (t): ChildBooking[] => [
  {
    alternates: ALTERNATES,
    childProducts: _.flatMap(
      _.times(ALTERNATES),
      (alternate) => _.flatMap(_.times(5, (index) => ([
        {
          alternate: alternate + 1,
          dayOfWeek: DAYS_OF_WEEK_LIST[index],
          id: v4(),
          product: {
            name: t('module:RegistrationForm:fields:bookingPattern:session'),
          },
          type: ChildProductTypes.REGULAR_SESSION,
        },
        {
          alternate: alternate + 1,
          dayOfWeek: DAYS_OF_WEEK_LIST[index],
          id: v4(),
          product: {
            name: t('module:RegistrationForm:fields:bookingPattern:discount'),
          },
          type: ChildProductTypes.REGULAR_LINK_DISCOUNT,
        },
      ]))),
    ),
  },
]

const renderDummyChildSessionsV2 = (t) => [
  {
    formattedPlans: _.times(5, (index) => ({
      dayOfWeek: DAYS_OF_WEEK_LIST[index],
      plans: [
        {
          archived: false,
          endTime: 61200000,
          sessionName: t('module:RegistrationForm:fields:bookingPattern:session'),
          startTime: 32400000,
        },
      ],
    })),
    id: 1,
  },
]

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>

interface BookingPatternProps {
  child: Child
  isFinanceV3Enabled: boolean
  isPreview?: boolean
  nurserySettings?: NurserySettings
}

type BookingPatternFullProps = BookingPatternProps
  & PropsFromRedux
  & withChildBookingServiceProps
  & withChildSessionsServiceProps
  & withRouterProps

const BookingPattern = ({
  child,
  childBookingActions,
  childBookings,
  childSessions,
  childSessionsActions,
  isFetchingSessions,
  isFinanceV3Enabled,
  isPreview,
  location,
  nurserySettings,
}: BookingPatternFullProps) => {
  const { query } = location
  const { t: token } = query

  useEffect(() => {
    if (!isFinanceV3Enabled && child && !isPreview) {
      const criteria = childSessionsSelectors.getCriteria({ archived: 0 })

      childSessionsActions.groupedByList(child.id, {
        criteria,
        includes: ['attendancePeriod', 'invoiced', 'fundingAssigned'],
      }, () => {}, token)
    }

    if (isFinanceV3Enabled && child && !isPreview) {
      const criteria = childBookingSelectors.getCriteria({
        childId: child.id,
      })

      childBookingActions.list({
        params: {
          criteria,
          groups: CHILD_BOOKING_GROUPS,
          order: {
            sortField: 'startDate',
            sortOrder: 'DESC',
          },
        },
        recursively: true,
      })
    }
  }, [child, isFinanceV3Enabled])

  const renderWeekDay = (dayOfWeekIndex: number, childBooking: ChildBooking, alternate: number) => {
    const dayOfWeek = _.find(DAYS_OF_WEEK_LIST, (item, i) => i === dayOfWeekIndex)
    const sessions = _.filter(childBooking.childProducts, (item: ChildProduct) => (
      alternate + 1 === item.alternate
      && item.type === ChildProductTypes.REGULAR_SESSION
      && item.dayOfWeek === dayOfWeek
    )) as ChildProductRegularSession[]

    const discounts = _.filter(childBooking.childProducts, (item: ChildProduct) => (
      alternate + 1 === item.alternate
      && (
        item.type === ChildProductTypes.REGULAR_LINK_DISCOUNT
        || item.type === ChildProductTypes.REGULAR_AMOUNT_DISCOUNT
      )
      && item.dayOfWeek === dayOfWeek
    )) as (ChildProductRegularPercentageDiscount | ChildProductRegularAmountDiscount)[]

    return (
      <StyledWeekDay key={dayOfWeek}>
        <StyledHeader>
          <Typography fontSize={14} transform="uppercase" ellipsis>
            {i18n.t(`global:shortDayNames:${dayOfWeekIndex + 1}`)}
          </Typography>
        </StyledHeader>
        <Flex flex={1} padding="8px">
          {0 < sessions.length ? (
            <ul>
              {_.map(sessions, (session: ChildProductRegularSession) => (
                <li key={session.id}>
                  <Typography fontSize={14} bold>
                    {session.product.name}
                  </Typography>
                </li>
              ))}
            </ul>
          ) : (
            <Typography color={NEUTRAL_COLOURS.GRAY} fontSize={14}>
              {i18n.t('module:RegistrationForm:fields:bookingPattern:noSessions')}
            </Typography>
          )}
        </Flex>
        {0 < discounts.length && (
          <StyledFooter>
            {_.map(discounts, (discount: ChildProductRegularLinkDiscount | ChildProductRegularAmountDiscount) => (
              <Typography fontSize={12} key={discount.id}>
                {discount.type === ChildProductTypes.REGULAR_LINK_DISCOUNT && discount.product.name}
                {discount.type === ChildProductTypes.REGULAR_AMOUNT_DISCOUNT && (
                  <SubdomainCurrencyProvider key={discount.id}>
                    {({ getFormattedCurrencyValue }) => (
                      <React.Fragment>
                        {discount.settings.name}
                        {' '}
                        {getFormattedCurrencyValue(discount.settings.value / 100)}
                      </React.Fragment>
                    )}
                  </SubdomainCurrencyProvider>
                )}
              </Typography>
            ))}
          </StyledFooter>
        )}
      </StyledWeekDay>
    )
  }

  const renderChildSession = (childSession) => {
    const { attendancePeriod, endDate, id, isOngoing, startDate } = childSession

    const startDateText = moment(startDate).format(DISPLAY_SHORT_DATE_FORMAT)
    const endDateText = isOngoing
      ? i18n.t('module:Children:Child:BookingPattern:Sessions:List:Current:ongoing')
      : moment(endDate).format(DISPLAY_SHORT_DATE_FORMAT)

    let attendancePeriodText = `${attendancePeriod?.name}${attendancePeriod?.isArchived
      ? ` (${i18n.t('global:archived')})`
      : ''}`

    if (isPreview) {
      attendancePeriodText = ''
    }

    return (
      <React.Fragment key={id}>
        <Space space="20px" />
        <Typography fontSize={18} fontWeight="600" margin="0 0 10px">
          {isPreview
            ? i18n.t('module:RegistrationForm:fields:bookingPattern:datePeriod')
            : `${startDateText} - ${endDateText}`}
        </Typography>
        <ChildSessionsList
          attendancePeriodText={attendancePeriodText}
          childSession={childSession}
          isPreview={isPreview}
        />
        <Space space="-50px" />
      </React.Fragment>
    )
  }

  const renderFunds = (childBooking: ChildBooking, alternate: number) => {
    const fundings = _.map(_.flow(
      (i) => _.groupBy(i, (item) => item.product.id),
      (groupedItems) => groupedItems,
    )(
      _.filter(childBooking.childProducts, (item) => (
        item.alternate === alternate + 1
        && ChildProductTypes.REGULAR_ALLOCATED_FUNDING === item.type
      )),
    ))

    return (
      <Typography fontSize={14} lineHeight={18}>
        {!fundings.length ? '-' : null}
        {_.map(fundings, (fundingsGroup) => {
          let times = 0
          const item = fundingsGroup[0]

          _.each(fundingsGroup, (element) => {
            const { settings } = element || {}
            const { defaultAllocations } = settings || {}

            _.each(defaultAllocations, (allocation) => {
              _.each(allocation?.times, (time) => {
                times += time.endTime - time.startTime
              })
            })
          })

          const hours = Math.floor(Math.round((times / 1000 / 60 / 60) * 100) / 100)
          const minutes = +moment(times).utc().format('m')

          return (
            <div key={item.id}>
              {hours ? `${i18n.t('global:Hours', { value: hours })}` : null}
              {minutes ? ` ${i18n.t('global:Minutes', { value: minutes })}` : null}
              {hours || minutes ? ' - ' : null}
              {item.product.name}
            </div>
          )
        })}
      </Typography>
    )
  }

  if (!isFinanceV3Enabled) {
    if (isFetchingSessions) {
      return <Spinner position="flex-start" />
    }

    let finalChildSessions = []

    if (childSessions && nurserySettings) {
      const formattedOpeningDays = getFormattedOpeningDays(nurserySettings)

      finalChildSessions = childSessionsSelectors.getFormattedSessions(childSessions.current, formattedOpeningDays)
    }

    if (isPreview) {
      const dummyChildSessions = renderDummyChildSessionsV2(i18n.t)

      finalChildSessions = dummyChildSessions
    }

    if (!finalChildSessions?.length) {
      return (
        <Banner.Warning>
          {i18n.t('module:Children:Child:About:Profile:RegistrationForm:emptyBookingPattern')}
        </Banner.Warning>
      )
    }

    return (
      _.map(finalChildSessions, renderChildSession)
    )
  }

  if (!childBookings?.length && !isPreview) {
    return (
      <Banner.Warning>
        {i18n.t('module:Children:Child:About:Profile:RegistrationForm:emptyBookingPattern')}
      </Banner.Warning>
    )
  }

  let finalChildBookings = childBookings

  if (isPreview) {
    const dummyChildBookings = renderDummyChildBookingsV3(i18n.t)

    finalChildBookings = dummyChildBookings
  }

  return _.map(finalChildBookings, (childBooking: ChildBooking) => (
    <StyledWrapper key={childBooking.id}>
      <Flex alignItems="center" margin="0 0 15px">
        {!isPreview && (
          <Typography fontSize={18} variant="div" bold>
            {moment(childBooking.startDate).format(DISPLAY_DATE_SHORT_MONTH_NAME_FORMAT)}
            {' - '}
            {childBooking.endDate
              ? moment(childBooking.endDate).format(DISPLAY_DATE_SHORT_MONTH_NAME_FORMAT)
              : (
                <Typography fontSize={18} variant="span" inline>
                  {moment(childBooking.child?.leavingDate).format(DISPLAY_DATE_SHORT_MONTH_NAME_FORMAT)}
                  <Typography fontSize={14} variant="span" bold inline>
                    {` (${i18n.t('module:Finance:RegularBookings:List:untilChildLeaves')})`}
                  </Typography>
                </Typography>
              )}
            <Typography inline>
              {' '}
              {childBooking.attendancePeriod?.name}
            </Typography>
          </Typography>
        )}
        {isPreview && (
          <Typography fontSize={18} variant="div" bold>
            {i18n.t('module:RegistrationForm:fields:bookingPattern:preview')}
          </Typography>
        )}
      </Flex>
      {_.times(childBooking.alternates, (alternate: number) => (
        <React.Fragment key={alternate}>
          {0 < alternate && <Space space="40px" />}
          <Flex flexDirection="column" key={alternate} margin="0 0 5px">
            <Flex alignItems="center" margin="0 0 15px">
              <Flex flex={1}>
                <Typography color={NEUTRAL_COLOURS.GRAY} fontSize={18}>
                  {i18n.t('module:RegistrationForm:fields:bookingPattern:week', { weekNumber: alternate + 1 })}
                </Typography>
              </Flex>
            </Flex>
            <StyledWeekContainer>
              {_.times(7, (dayOfWeek) => (
                renderWeekDay(dayOfWeek, childBooking, alternate)
              ))}
            </StyledWeekContainer>
          </Flex>
          <DefinitionList>
            <DefinitionList.Item
              label={(
                <Typography color={NEUTRAL_COLOURS.GRAY} fontSize={14}>
                  {i18n.t('module:RegistrationForm:fields:bookingPattern:funds')}
                </Typography>
              )}
              value={renderFunds(childBooking, alternate)}
            />
          </DefinitionList>
        </React.Fragment>
      ))}
    </StyledWrapper>
  ))
}

const enhance = compose(
  withChildSessionsService,
  withChildBookingService,
  withRouter,
  connector,
)

export default enhance(BookingPattern)
